import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, FileText, Video, Music, Image as ImageIcon, Loader2, Plus, HardDrive, X, Search, ExternalLink } from "lucide-react";

const iconFor = (k) => (k === "video" ? Video : k === "audio" ? Music : ImageIcon);

export default function Editor() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [docs, setDocs] = useState([]);
  const [activeDocId, setActiveDocId] = useState("");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [assets, setAssets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  const [driveQuery, setDriveQuery] = useState("");
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveError, setDriveError] = useState("");
  const [drivePickerEnabled, setDrivePickerEnabled] = useState(false);

  useEffect(() => {
    api.get("/config").then(({ data }) => setDrivePickerEnabled(!!data.drive_picker_enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/projects").then(({ data }) => {
      setProjects(data);
      if (data.length && !projectId) setProjectId(data[0].project_id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/documents`).then(({ data }) => {
      setDocs(data);
      if (data.length) setActiveDocId(data[0].document_id);
      else setActiveDocId("");
    });
  }, [projectId]);

  useEffect(() => {
    if (!activeDocId) {
      setTitle(""); setContent(""); setAssets([]); return;
    }
    api.get(`/documents/${activeDocId}`).then(({ data }) => {
      setTitle(data.title); setContent(data.content); setAssets(data.linked_assets || []);
      setDirty(false);
    });
  }, [activeDocId]);

  const save = useCallback(async () => {
    if (!activeDocId) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/documents/${activeDocId}`, { title, content, linked_assets: assets });
      setDocs((ds) => ds.map((d) => d.document_id === data.document_id ? { ...d, title: data.title, updated_at: data.updated_at } : d));
      setDirty(false);
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }, [activeDocId, title, content, assets]);

  const wordCount = useMemo(() => content.trim().split(/\s+/).filter(Boolean).length, [content]);
  const timestamps = useMemo(() => (content.match(/\[\d{1,2}:\d{2}\]/g) || []).length, [content]);

  const createDoc = async () => {
    if (!projectId) return toast.error("Create/select a project first");
    const t = prompt("Document title?");
    if (!t) return;
    const { data } = await api.post(`/projects/${projectId}/documents`, { title: t, content: "# New Script\n\n" });
    setDocs((ds) => [data, ...ds]);
    setActiveDocId(data.document_id);
  };

  const addAsset = () => {
    const label = prompt("Asset label (e.g. cold_open.mov)?");
    if (!label) return;
    const kind = prompt("Kind? video / audio / graphic", "video") || "graphic";
    setAssets((a) => [...a, { kind, label, url: `drive://mock/${label}` }]);
    setDirty(true);
  };

  const openDrivePicker = async () => {
    setDrivePickerOpen(true);
    setDriveError("");
    await searchDrive("");
  };

  const searchDrive = async (q) => {
    setDriveLoading(true);
    setDriveError("");
    try {
      const { data } = await api.get("/drive/files", { params: q ? { q } : {} });
      setDriveFiles(data.files || []);
    } catch (e) {
      setDriveFiles([]);
      setDriveError(e?.response?.data?.detail || "Could not load Drive files. Connect Google under Analytics first.");
    } finally { setDriveLoading(false); }
  };

  const attachDriveFile = (f) => {
    // Avoid duplicates by drive id
    if (assets.some((a) => a.drive_id === f.id)) {
      toast.info(`${f.name} is already attached`);
      return;
    }
    setAssets((a) => [...a, {
      kind: f.kind === "doc" || f.kind === "sheet" || f.kind === "slides" ? "graphic" : f.kind,
      label: f.name,
      url: f.url,
      drive_id: f.id,
      thumbnail: f.thumbnail,
      mime_type: f.mime_type,
    }]);
    setDirty(true);
    setDrivePickerOpen(false);
    toast.success(`${f.name} linked from Drive`);
  };

  return (
    <div className="min-h-screen">
      {/* Sub-header */}
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="px-4 md:px-8 min-h-16 flex flex-wrap items-center gap-2 md:gap-4 py-3 md:py-0">
          <FileText size={16} strokeWidth={1.5} className="text-[#FF3B30]" />
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="sd-input h-9 max-w-[240px]"
            data-testid="editor-project-select"
          >
            {projects.map((p) => <option key={p.project_id} value={p.project_id}>{p.title}</option>)}
          </select>
          <select
            value={activeDocId}
            onChange={(e) => setActiveDocId(e.target.value)}
            className="sd-input h-9 max-w-[280px]"
            data-testid="editor-doc-select"
          >
            {docs.map((d) => <option key={d.document_id} value={d.document_id}>{d.title}</option>)}
            {docs.length === 0 && <option>No documents</option>}
          </select>
          <button onClick={createDoc} className="sd-btn sd-btn-ghost h-9" data-testid="editor-new-doc-btn">
            <Plus size={14} strokeWidth={1.5} /> New doc
          </button>
          <div className="ml-auto flex items-center gap-4">
            <div className="sd-label">
              {wordCount} WORDS · {timestamps} MARKERS {dirty ? "· UNSAVED" : ""}
            </div>
            <button
              onClick={save}
              disabled={saving || !activeDocId || !dirty}
              className="sd-btn sd-btn-primary h-9"
              data-testid="editor-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.5} />}
              Save
            </button>
          </div>
        </div>
      </div>

      {!activeDocId ? (
        <div className="p-20 text-center">
          <div className="sd-label mb-4">EMPTY</div>
          <div className="font-display text-4xl">No document selected.</div>
          <div className="mt-3 text-zinc-500">Create a new document above or pick one from the dropdown.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px]">
          {/* Editor pane */}
          <div className="px-4 sm:px-8 lg:px-16 py-10 lg:py-16">
            <input
              value={title}
              onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
              className="w-full bg-transparent outline-none font-display text-4xl md:text-5xl lg:text-6xl leading-[1] tracking-tight placeholder:text-zinc-700"
              placeholder="Untitled script"
              data-testid="editor-title-input"
            />
            <div className="sd-label mt-6">SCRIPT</div>
            <textarea
              value={content}
              onChange={(e) => { setContent(e.target.value); setDirty(true); }}
              className="mt-4 w-full min-h-[520px] bg-transparent border-l-2 border-[#27272a] pl-6 outline-none font-sans text-[17px] leading-[1.75] text-zinc-200 placeholder:text-zinc-600 resize-none"
              placeholder="Write your script here. Use markdown headings and [00:00] timestamps."
              data-testid="editor-content-textarea"
              style={{ fontFamily: "Satoshi, ui-sans-serif" }}
            />
          </div>

          {/* Linked assets rail */}
          <aside className="border-l border-[#27272a] p-6 lg:sticky lg:top-16 lg:self-start lg:h-[calc(100vh-64px)] overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="sd-label">LINKED ASSETS</div>
              <div className="flex items-center gap-1">
                {drivePickerEnabled && (
                  <button
                    onClick={openDrivePicker}
                    className="text-zinc-500 hover:text-white p-1 -mr-1"
                    data-testid="editor-drive-picker-btn"
                    title="Attach from Google Drive"
                  >
                    <HardDrive size={13} strokeWidth={1.5} />
                  </button>
                )}
                <button onClick={addAsset} className="text-zinc-500 hover:text-white p-1" data-testid="editor-add-asset-btn" title="Add manually">
                  <Plus size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {assets.length === 0 && (
                <div className="text-xs text-zinc-600">
                  No assets linked yet.
                  {drivePickerEnabled ? (
                    <button
                      onClick={openDrivePicker}
                      className="mt-2 block text-[#FF3B30] hover:underline"
                      data-testid="editor-drive-picker-empty-btn"
                    >
                      Browse Google Drive →
                    </button>
                  ) : (
                    <button
                      onClick={addAsset}
                      className="mt-2 block text-[#FF3B30] hover:underline"
                      data-testid="editor-add-asset-empty-btn"
                    >
                      Add an asset manually →
                    </button>
                  )}
                </div>
              )}
              {assets.map((a, i) => {
                const Icon = iconFor(a.kind);
                const InnerContent = (
                  <>
                    {a.thumbnail ? (
                      <img src={a.thumbnail} alt="" className="w-10 h-10 object-cover shrink-0 border border-[#27272a]" referrerPolicy="no-referrer" />
                    ) : (
                      <Icon size={16} strokeWidth={1.5} className="text-[#FF3B30] shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-mono truncate">{a.label}</div>
                      <div className="sd-label flex items-center gap-1">
                        {a.kind}
                        {a.drive_id && <span className="text-[#34C759]">· DRIVE</span>}
                      </div>
                    </div>
                    {a.drive_id && <ExternalLink size={11} strokeWidth={1.5} className="text-zinc-500 shrink-0" />}
                  </>
                );
                return a.drive_id && a.url ? (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-[#27272a] p-3 flex items-center gap-3 hover:border-zinc-500 transition-colors"
                    data-testid={`asset-${i}`}
                  >
                    {InnerContent}
                  </a>
                ) : (
                  <div key={i} className="border border-[#27272a] p-3 flex items-center gap-3 hover:border-zinc-500 transition-colors" data-testid={`asset-${i}`}>
                    {InnerContent}
                  </div>
                );
              })}
            </div>

            <div className="sd-label mt-10">STRUCTURE</div>
            <ul className="mt-4 space-y-2 text-xs text-zinc-400">
              {(content.match(/^#+\s.*$/gm) || []).slice(0, 20).map((line, i) => (
                <li key={i} className="truncate hover:text-white transition-colors cursor-default">
                  {line.replace(/^#+\s/, "")}
                </li>
              ))}
              {(content.match(/^#+\s.*$/gm) || []).length === 0 && (
                <li className="text-zinc-600">Add # headings to build an outline.</li>
              )}
            </ul>
          </aside>
        </div>
      )}

      {/* Google Drive Picker Modal */}
      {drivePickerOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setDrivePickerOpen(false)}
        >
          <div
            className="bg-[#0e0e0f] border border-[#27272a] w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="drive-picker-modal"
          >
            <div className="p-6 border-b border-[#27272a] flex items-start justify-between">
              <div>
                <div className="sd-label mb-2 text-[#FF3B30]">● GOOGLE DRIVE</div>
                <h3 className="font-display text-2xl">Attach a Drive file</h3>
              </div>
              <button
                onClick={() => setDrivePickerOpen(false)}
                className="text-zinc-500 hover:text-white"
                data-testid="drive-picker-close-btn"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); searchDrive(driveQuery); }}
              className="p-4 border-b border-[#27272a] flex gap-2"
            >
              <div className="relative flex-1">
                <Search size={13} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  autoFocus
                  value={driveQuery}
                  onChange={(e) => setDriveQuery(e.target.value)}
                  placeholder="Search your Drive…"
                  className="sd-input h-10 pl-9"
                  data-testid="drive-picker-search-input"
                />
              </div>
              <button type="submit" className="sd-btn sd-btn-primary h-10" data-testid="drive-picker-search-btn">
                {driveLoading ? <Loader2 size={13} className="animate-spin" /> : "Search"}
              </button>
            </form>

            <div className="flex-1 overflow-y-auto p-2">
              {driveError && (
                <div className="p-6 text-sm text-[#FF3B30] font-mono" data-testid="drive-picker-error">
                  × {driveError}
                </div>
              )}
              {!driveError && driveLoading && (
                <div className="p-10 flex items-center justify-center text-zinc-500">
                  <Loader2 size={16} className="animate-spin mr-2" /> Loading Drive files…
                </div>
              )}
              {!driveError && !driveLoading && driveFiles.length === 0 && (
                <div className="p-10 text-center text-sm text-zinc-500" data-testid="drive-picker-empty">
                  No files found. Try another search.
                </div>
              )}
              {!driveError && !driveLoading && driveFiles.length > 0 && (
                <div className="divide-y divide-[#27272a]">
                  {driveFiles.map((f) => {
                    const Icon = iconFor(f.kind === "audio" ? "audio" : f.kind === "video" ? "video" : "graphic");
                    return (
                      <button
                        key={f.id}
                        onClick={() => attachDriveFile(f)}
                        className="w-full text-left p-3 flex items-center gap-3 hover:bg-[#141414] transition-colors"
                        data-testid={`drive-file-${f.id}`}
                      >
                        {f.thumbnail ? (
                          <img src={f.thumbnail} alt="" className="w-10 h-10 object-cover border border-[#27272a] shrink-0" referrerPolicy="no-referrer" />
                        ) : (
                          <div className="w-10 h-10 border border-[#27272a] bg-[#141414] flex items-center justify-center shrink-0">
                            <Icon size={15} strokeWidth={1.5} className="text-[#FF3B30]" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate">{f.name}</div>
                          <div className="sd-label mt-0.5">
                            {f.kind} · {f.modified ? new Date(f.modified).toLocaleDateString() : ""}
                          </div>
                        </div>
                        <div className="sd-label text-zinc-500">ATTACH →</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
